import React from "react";
import { Seo } from "@/components/shared/Seo";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  BadgeDollarSign,
  Scale,
  UserCheck,
} from "lucide-react";
import { CALENDAR_URL } from "@/constants/urls";
import NotFound from "./NotFound";

const BASE = "https://www.adiastra.com";

/**
 * ⚠️ THE ONLY THING LEFT IS THE `name` FIELD BELOW.
 *
 * Routing, prerendering, sitemap, nav links, and Person schema are all wired
 * and gated on IS_FOUNDER_CONFIGURED. Fill in `name` (and ideally jobTitle +
 * sameAs) and the page goes live on the next deploy — no other file needs to
 * change. Until then /about returns a real 404 and is absent from the sitemap
 * and the nav, so these placeholders can never reach a visitor.
 *
 * The bio is deliberately unwritten rather than drafted-and-edited: every
 * field here is a factual claim about a real person, and the whole value of
 * this page is that those claims are verifiable. `bio`, `why`, `credentials`
 * and `photo` are all optional — the page reads correctly as a lean
 * name-and-principles page without them, and richer with them.
 */
const FOUNDER = {
  /** REQUIRED. Full name as it should appear publicly and in schema. */
  name: "Adi Kol",
  /** e.g. "Founder & Principal Consultant" */
  jobTitle: "Founder & Principal Consultant",
  /** Optional. 2–4 sentences: career before this, and why the expertise is real. */
  bio: "Adi builds AI products as well as advising on them. He is the founder of Helos.ai, an AI-powered licensing and rights-enforcement platform for creators and brands, and a principal agent at Compass. Before that, a career across marketing, brand, and business development for tech startups and international companies, and service as an army veteran in a highly technical unit.",
  /** Optional. Why this business, why South Florida. */
  why: "That combination is why this practice works the way it does. The automation gets built by someone who ships software for a living, and it gets scoped by someone who runs an appointment-driven local business himself — so the advice comes from having sat on your side of it. He works out of Boca Raton, which is why on-site means an actual drive rather than a video call.",
  /** Optional. Real, verifiable only. Leave empty rather than padding. */
  credentials: [] as string[],
  /** Optional. e.g. "/founder.jpg" — drop the file in public/. */
  photo: "",
  /**
   * Personal LinkedIn, plus the company page and Google Business Profile once
   * they exist. Absolute URLs only — these are rendered through `new URL()`.
   */
  sameAs: ["https://www.linkedin.com/in/adi-kol-46078522"] as string[],
};

/**
 * The single switch. Everything about /about — route, prerender, sitemap
 * entry, nav links — hangs off this, so the page cannot publish half-built.
 */
export const IS_FOUNDER_CONFIGURED = FOUNDER.name.trim().length > 0;

const principles = [
  {
    icon: UserCheck,
    title: "The consultant does the work",
    body: "You work with the person who walks your floor, builds the automations, and trains your team. Not an account manager, not a call center, not an offshore pod that inherits the project after the sale.",
  },
  {
    icon: MapPin,
    title: "On-site, in South Florida",
    body: "We drive to you across Miami-Dade, Broward, and Palm Beach. An hour watching your front desk on a real Tuesday morning tells us more than any questionnaire, and it's the reason we work locally rather than remotely.",
  },
  {
    icon: BadgeDollarSign,
    title: "Published pricing",
    body: "The rate card is on the website. You will never need to book a call to find out what something costs, and every quote is fixed in writing before work starts rather than shaped by how the sales conversation went.",
  },
  {
    icon: Scale,
    title: "Honest math",
    body: "Savings are computed from your numbers with every assumption shown. No inflated ROI, no score floors, no invented case studies. If your business is already lean, the assessment will say so — that's a real answer, and it's cheaper to hear it for free.",
  },
];

const structuredData = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Person",
      "@id": `${BASE}/about#founder`,
      name: FOUNDER.name,
      jobTitle: FOUNDER.jobTitle,
      description: FOUNDER.bio,
      worksFor: { "@id": `${BASE}/#business` },
      ...(FOUNDER.photo ? { image: `${BASE}${FOUNDER.photo}` } : {}),
      ...(FOUNDER.sameAs.length ? { sameAs: FOUNDER.sameAs } : {}),
      knowsAbout: [
        "AI automation",
        "business process automation",
        "AI implementation",
        "AI training",
      ],
    },
    {
      "@type": "AboutPage",
      "@id": `${BASE}/about#page`,
      url: `${BASE}/about`,
      mainEntity: { "@id": `${BASE}/about#founder` },
      about: { "@id": `${BASE}/#business` },
    },
  ],
});

const About: React.FC = () => {
  // Belt and braces: the route is only prerendered and only linked when
  // configured, but if this ever renders unconfigured it must not show
  // half-filled founder details.
  if (!IS_FOUNDER_CONFIGURED) return <NotFound />;

  return (
    <>
      <Seo>
        <title>About Adi Astra | South Florida AI Automation Consulting</title>
        <meta
          name="description"
          content="Who you're actually working with. Adi Astra is a solo South Florida AI automation practice — on-site across Miami-Dade, Broward and Palm Beach, with published pricing and honest math."
        />
        <link rel="canonical" href={`${BASE}/about`} />
      </Seo>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData()) }}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base font-semibold text-primary tracking-wide uppercase">
            About
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            Who you&rsquo;re actually working with
          </h1>
          <p className="mt-6 text-xl text-muted-foreground">
            Adi Astra is a one-person practice, on purpose. The person who
            assesses your business is the person who builds the automations and
            trains your team.
          </p>
        </div>

        <section className="mt-16 max-w-3xl mx-auto">
          <div className="rounded-lg border bg-card p-8">
            {FOUNDER.photo && (
              <img
                src={FOUNDER.photo}
                alt={`${FOUNDER.name}, ${FOUNDER.jobTitle} at Adi Astra`}
                className="w-32 h-32 rounded-full object-cover mb-6"
                width={128}
                height={128}
              />
            )}
            <h2 className="text-2xl font-semibold">{FOUNDER.name}</h2>
            <p className="text-muted-foreground">{FOUNDER.jobTitle}</p>
            {FOUNDER.bio && <p className="mt-4">{FOUNDER.bio}</p>}
            {FOUNDER.why && <p className="mt-4">{FOUNDER.why}</p>}
            {FOUNDER.credentials.length > 0 && (
              <ul className="mt-6 space-y-2">
                {FOUNDER.credentials.map((c) => (
                  <li key={c} className="text-sm text-muted-foreground">
                    {c}
                  </li>
                ))}
              </ul>
            )}
            {FOUNDER.sameAs.length > 0 && (
              <p className="mt-6 text-sm">
                {FOUNDER.sameAs.map((url) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer me"
                    className="text-primary hover:text-primary/80 mr-4"
                  >
                    {new URL(url).hostname.replace(/^www\./, "")}
                  </a>
                ))}
              </p>
            )}
          </div>
        </section>

        <section className="mt-20 max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-4">
            How we work, and why
          </h2>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-10">
            Four commitments that shape every engagement. They&rsquo;re also the
            reasons a client would pick a local one-person practice over a
            larger firm.
          </p>
          <div className="grid gap-8 md:grid-cols-2">
            {principles.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="rounded-lg border bg-card text-card-foreground shadow-sm p-8"
                >
                  <div className="mb-4">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{p.title}</h3>
                  <p className="text-muted-foreground">{p.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-8">
            What we won&rsquo;t do
          </h2>
          <ul className="space-y-4">
            <li className="rounded-lg border bg-card p-6">
              <strong>Publish numbers we can&rsquo;t stand behind.</strong>{" "}
              There are no invented case studies, testimonials, or client counts
              anywhere on this site. When there are real ones, they will appear
              with names attached.
            </li>
            <li className="rounded-lg border bg-card p-6">
              <strong>Sell you automation you don&rsquo;t need.</strong> The
              free assessment can tell you your business is already efficient,
              and if the intro call shows automation isn&rsquo;t worth it for
              you, we&rsquo;ll tell you that instead of selling it anyway.
            </li>
            <li className="rounded-lg border bg-card p-6">
              <strong>Leave you dependent on us.</strong> Builds happen in your
              accounts, on your stack, and training your team to run it is part
              of the work rather than an upsell.
            </li>
          </ul>
        </section>

        <section className="mt-20 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-semibold mb-4">Where we work</h2>
          <p className="text-lg text-muted-foreground mb-8">
            On-site across Miami-Dade, Broward, and Palm Beach counties — Miami,
            Fort Lauderdale, Boca Raton, West Palm Beach, and everywhere
            between.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md text-md font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 py-3"
            >
              Take the free test <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href={CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-md text-md font-medium border border-input bg-background hover:bg-accent h-12 px-6 py-3"
            >
              Book an intro call
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
